import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ProcessStepBar } from "../../../../common/layout/ProcessStepBar";
import { WizardActionsMenu } from "./components/WizardActionsMenu";
import { hasChangeInFlight } from "../../../sourcing-event/changes/changeScope";
import { EventHeader } from "./layout/EventHeader";
import { MobileStepDrawer } from "./layout/MobileStepDrawer";
import { AwardCriteriaStep } from "./steps/AwardCriteriaStep";
import { ClassificationStep } from "./steps/ClassificationStep";
import { DocumentsStep } from "./steps/DocumentsStep";
import { InfoStep } from "./steps/InfoStep";
import { MinimumRequirementsStep } from "./steps/MinimumRequirementsStep";
import { ProcedureStep } from "./steps/ProcedureStep";
import { QualificationStep } from "./steps/QualificationStep";
import { SummaryStep } from "./steps/SummaryStep";
import { DetailsStep } from "./steps/private/DetailsStep";
import { InvitationsStep } from "./steps/private/InvitationsStep";
import { ProductsStep } from "./steps/private/ProductsStep";
import { ReviewAndPublishStep } from "./steps/private/ReviewAndPublishStep";
import {
  bffApi,
  useConfirmSourcingEventClassificationMutation,
  useGetSourcingEventReadinessQuery,
  useUpdateSourcingEventMutation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../autogen/bff-api";
import { useStepNavigation } from "./hooks/useStepNavigation";
import {
  resolveInclusions,
  resolveProcess,
  stepNeighbors,
  useStepLabel,
  useStepShortLabel,
  WIZARD_STEPS,
  type OrgWizardAccess,
  type PublicEventStep,
} from "./wizardSteps";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
  useEntityState,
  useRefusedField,
} from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../../common/subscription/useSub";
import { useRunsPublicProcurements } from "../../../../common/organization/useRunsPublicProcurements";
import { ChangeResponsiblePersonButton } from "../../../sourcing-event/ChangeResponsiblePersonButton";
import { saveWithVersionConflictRetry } from "./saveWithVersionConflictRetry";
import { urls } from "../../../../urls";
import { CHANGE_NOT_OPEN_TOAST, isChangeNotOpenRefusal } from "./changeNotOpenRefusal";
import { useSuggestedRequestDeadline } from "./hooks/useSuggestedRequestDeadline";
import { useSuggestedBidDeadline } from "./hooks/useSuggestedBidDeadline";

interface Props {
  eventId: string;
  event: SourcingEventDto;
  refetch: () => void;
}

const INFO_STEP = WIZARD_STEPS[0];

export const EditSourcingEventWizardForm = ({ eventId, event, refetch }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const editorErrorConfig = useApiErrorEditorConfig();
  const { trigger, triggerSoft } = useApiError();
  const toast = useToast();
  const navigate = useNavigate();
  const [updateEvent] = useUpdateSourcingEventMutation();
  const [confirmClassification, { isLoading: isConfirming }] = useConfirmSourcingEventClassificationMutation();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const sub = useSub();
  const runsPublicProcurements = useRunsPublicProcurements();
  const stepLabel = useStepLabel();
  const stepShortLabel = useStepShortLabel();
  const topRef = useRef<HTMLDivElement>(null);

  // Subscribed at the orchestrator level so the readiness cache survives
  // step navigation; sections re-subscribe via useReadinessByField and RTK
  // Query dedupes. Surface fetch failures via the soft toast — without this
  // the badges silently disappear and SummaryStep's Publish gate engages
  // with no user-visible reason.
  const { error: readinessError } = useGetSourcingEventReadinessQuery({ eventId });
  useEffect(() => {
    if (readinessError) triggerSoft(readinessError);
  }, [readinessError, triggerSoft]);

  const config = useMemo(
    () => ({
      initialState: event,
      save: async (fields: Partial<SourcingEventPatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateEvent({
              eventId,
              sourcingEventPatchDto: retryFields as SourcingEventPatchDto,
            }).unwrap(),
          reloadLatest: async () => {
            await dispatch(
              bffApi.endpoints.getSourcingEvent.initiate(
                { eventId },
                { forceRefetch: true },
              ),
            ).unwrap();
          },
        }),
      ...editorErrorConfig,
      reportsWhileAwaited: isChangeNotOpenRefusal,
      onError: (e: unknown) => {
        if (!isChangeNotOpenRefusal(e)) return editorErrorConfig.onError(e);
        coordinatorRegistry.get(eventId)?.discardPendingChanges();
        navigate(urls.app.sourcingEvents.view.go(eventId));
        if (toast.isActive(CHANGE_NOT_OPEN_TOAST)) return;
        toast({
          id: CHANGE_NOT_OPEN_TOAST,
          status: "warning",
          duration: null,
          isClosable: true,
          title: t("This change is no longer open"),
          description: t(
            "It was published or discarded in another tab or by a colleague, so what you just did was not saved. The page now shows the request as it stands. Choose «Change the request» to start a new change.",
          ),
        });
      },
    }),
    [dispatch, event, eventId, updateEvent, editorErrorConfig, toast, t, navigate],
  );

  useCreateEditor<SourcingEventDto, SourcingEventPatchDto>(eventId, config);

  const optimisticEvent = useEntityState<SourcingEventDto>(eventId) ?? event;
  const refusedTitle = useRefusedField<SourcingEventDto>(eventId, "eventData.title");
  const access: OrgWizardAccess = {
    hasPublicProcurementAccess: runsPublicProcurements,
    hasProductAccess: sub.hasProductAccess,
  };
  const publicContext = optimisticEvent.eventContext.type === "Public" ? optimisticEvent.eventContext : null;
  const procedureType = publicContext?.procedureType ?? null;

  const { updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  useSuggestedRequestDeadline({
    publicContext,
    requestDeadline: optimisticEvent.eventData.requestDeadline ?? null,
    isSaving: fieldStatus("eventData.requestDeadline") === "saving",
    updateField,
  });
  useSuggestedBidDeadline({
    regulation: publicContext?.regulation ?? null,
    foaPart: publicContext?.foaPart ?? null,
    procedureType: procedureType ?? null,
    announcementDate: optimisticEvent.eventData.milestonePlan?.announcementDate ?? null,
    bidSubmissionDeadline: optimisticEvent.eventData.bidSubmissionDeadline ?? null,
    isSaving: fieldStatus("eventData.bidSubmissionDeadline") === "saving",
    updateField,
  });
  const inclusions = resolveInclusions(optimisticEvent.eventData);
  const process = resolveProcess(optimisticEvent.eventContext.type, access, { procedureType, inclusions });
  const steps = process?.steps ?? [INFO_STEP];
  const { currentStep, navigateToStep } = useStepNavigation(eventId, steps);

  // Reset scroll on step change; AppShell scrolls an inner <main>, not the window.
  useEffect(() => {
    const scroller = topRef.current?.closest("main");
    (scroller ?? window).scrollTo({ top: 0 });
  }, [currentStep]);

  const [visitedSteps, setVisitedSteps] = useState<ReadonlySet<PublicEventStep>>(() => new Set([currentStep]));
  useEffect(() => {
    setVisitedSteps((visited) => (visited.has(currentStep) ? visited : new Set(visited).add(currentStep)));
  }, [currentStep]);

  const confirmAndContinue = async () => {
    try {
      await coordinatorRegistry.flush(eventId);
      // Confirming derives the regime from the value, and on an already-classified procurement that
      // means converting it — which discards the other regime's work. Moving between the regimes is
      // the buyer's explicit call (ChangeRegimeButton), never a side effect of pressing Next.
      const confirmed =
        optimisticEvent.eventContext.type === "Unclassified"
          ? await confirmClassification({ eventId }).unwrap()
          : optimisticEvent;
      coordinatorRegistry.get(eventId)?.setServerState(confirmed);
      // Continue even when the regulation is still unanswered. It arrives from the firmakort for
      // almost every buyer, the panel on this step opens itself when it does not, and the
      // RegulationSet gate still blocks announcement — so holding here would only produce a button
      // that looks enabled and does nothing.
      const nextKey = resolveProcess(confirmed.eventContext.type, access, {
        procedureType: confirmed.eventContext.type === "Public" ? confirmed.eventContext.procedureType : null,
        inclusions,
      })?.steps[1]?.key;
      if (!nextKey) throw new Error(`No step follows the first for a ${confirmed.eventContext.type} procurement`);
      navigateToStep(nextKey);
    } catch (e) {
      trigger(e);
    }
  };

  const goTo = (step: PublicEventStep | undefined): (() => void) | undefined =>
    step ? () => navigateToStep(step) : undefined;
  const navPropsFor = (step: PublicEventStep) => {
    if (step === "info") {
      const { next } = process ? stepNeighbors("info", process.steps) : { next: undefined };
      return {
        nextStep: confirmAndContinue,
        nextStepLabel: next ? stepLabel(next) : t("Continue"),
        nextIsAction: !next,
        isNextLoading: isConfirming,
      };
    }
    const { previous, next } = stepNeighbors(step, steps);
    return { previousStep: goTo(previous), nextStep: goTo(next), nextStepLabel: next ? stepLabel(next) : undefined };
  };

  const stepRenderers: Record<PublicEventStep, () => ReactNode> = {
    info: () => <InfoStep eventId={eventId} {...navPropsFor("info")} />,
    classification: () => (
      <ClassificationStep
        eventId={eventId}
        isActive={currentStep === "classification"}
        {...navPropsFor("classification")}
      />
    ),
    procedure: () => <ProcedureStep eventId={eventId} {...navPropsFor("procedure")} />,
    qualification: () => (
      <QualificationStep
        eventId={eventId}
        isActive={currentStep === "qualification"}
        {...navPropsFor("qualification")}
      />
    ),
    awardCriteria: () => (
      <AwardCriteriaStep
        eventId={eventId}
        isActive={currentStep === "awardCriteria"}
        {...navPropsFor("awardCriteria")}
      />
    ),
    minimumRequirements: () => (
      <MinimumRequirementsStep
        eventId={eventId}
        isActive={currentStep === "minimumRequirements"}
        {...navPropsFor("minimumRequirements")}
      />
    ),
    documents: () => (
      <DocumentsStep
        eventId={eventId}
        event={event}
        refetch={refetch}
        isActive={currentStep === "documents"}
        {...navPropsFor("documents")}
      />
    ),
    summary: () => (
      <SummaryStep
        eventId={eventId}
        organization={selectedOrg}
        currentEditor={me}
        responsibleBuyer={optimisticEvent.responsiblePerson}
        responsibleEditControl={
          <ChangeResponsiblePersonButton
            canManage
            eventId={eventId}
            ownerOrgId={selectedOrg.id}
            homeDepartmentId={optimisticEvent.homeDepartment?.id}
            currentResponsible={optimisticEvent.responsiblePerson}
          />
        }
        previousStep={navPropsFor("summary").previousStep}
        navigateToStep={navigateToStep}
        steps={steps}
      />
    ),
    details: () => <DetailsStep eventId={eventId} {...navPropsFor("details")} />,
    products: () => <ProductsStep eventId={eventId} {...navPropsFor("products")} />,
    invitations: () => <InvitationsStep eventId={eventId} {...navPropsFor("invitations")} />,
    reviewAndPublish: () => (
      <ReviewAndPublishStep eventId={eventId} previousStep={navPropsFor("reviewAndPublish").previousStep} />
    ),
  };

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <Box ref={topRef} width="full" maxWidth="full">
      {process && <MobileStepDrawer steps={process.steps} currentStep={currentStep} navigateToStep={navigateToStep} />}
      <Flex justify="space-between" align="flex-start" gap="4">
        <Box flex="1" minW="0">
          <EventHeader
            title={(refusedTitle?.savedState ?? optimisticEvent).eventData.title}
            modifiedAt={optimisticEvent.modifiedAt}
            contextType={optimisticEvent.eventContext.type}
            isChangeInFlight={hasChangeInFlight(optimisticEvent)}
          />
        </Box>
        <Box flexShrink={0}>
          <WizardActionsMenu eventId={eventId} />
        </Box>
      </Flex>
      {process && (
        <Box display={{ base: "none", md: "block" }} py="8" overflowX="auto">
          <ProcessStepBar
            maxW="full"
            steps={process.steps.map((s) => ({ key: s.key, label: stepShortLabel(s.key) }))}
            currentKey={currentStep}
            onStepClick={(key) => navigateToStep(key as PublicEventStep)}
          />
        </Box>
      )}
      {process && (
        <Text display={{ base: "block", md: "none" }} py="4" fontSize="sm" fontWeight="semibold" color="smPrimary">
          {t("Step")} {currentIndex + 1}/{steps.length} · {stepLabel(currentStep)}
        </Text>
      )}
      {/* overflowX guards against a runaway child, but a focus ring is drawn outside the border box —
          and setting one overflow axis makes CSS compute the other to `auto`, so without room on
          every side the ring is sliced off top and bottom as well as left and right. */}
      <Box
        maxWidth="full"
        overflowX="hidden"
        px="1"
        mx="-1"
        py="1"
        pt={process ? undefined : "6"}
        pb={{ base: "20", md: "1" }}
      >
        {steps
          .filter(({ key }) => visitedSteps.has(key))
          .map(({ key }) => (
            <Box key={key} display={currentStep === key ? undefined : "none"}>
              {stepRenderers[key]()}
            </Box>
          ))}
      </Box>
    </Box>
  );
};
